import * as React from 'react';
import './styles.css';
import {withAITracking} from '@microsoft/applicationinsights-react-js';
import {inject} from 'mobx-react';
import {Header} from '../Header/Header';
import {reactPlugin} from '../../utilities/appInsightsConfig';
import {Grid, IconButton, Button, Typography} from "@material-ui/core";
import {UiStore} from "../../stores/ui-store";
import {apiFetch} from "../../utilities/apiFetch";
import MUIDataTable, {MUIDataTableColumnDef} from "mui-datatables";
import {DateTime} from "luxon";
import {KeyboardArrowLeft, KeyboardArrowRight, Sync, Refresh} from "@material-ui/icons";
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import tracker from '../Loader/loader-tracker';
import {debounce} from 'throttle-debounce';
import {ActionsMenu} from '../ActionsMenu/ActionsMenu';
import {ActionItem} from '../ActionsMenu/ActionItem';
import {SyncRadaroConfirmDialog} from './SyncRadaroConfirmDialog';

export interface IProps extends RouteComponentProps {
  uiStore?: UiStore
}

interface IJob {
  orderNumber: string;
  deliveryAddress: string;
  customerName: string;
  pickupAddress: string;
  purchaseOrderNumber: string;
  deliveryDate: string;
  deliveryTimeSlot: string;
  pallets: number;
  jobWeight: number;
  jobDetail: string;
  customerEmail: string;
  deliveryTruckType: string;
  freightOnly: boolean;
  radaroJobStatus: string;
}

interface IState {
  jobs: IJob[];
  date: DateTime;
  totalJobs: number;
  totalPallets: number;
  totalWeight: number;
  totalFreightOnly: number;
  syncConfirmDialogActive: boolean;
}

@inject('uiStore')
class RadaroJobs extends React.Component<IProps, IState> {
  private fetchJobsDebounced: () => void;

  constructor(props: IProps) {
    super(props);
    this.handlePreviousDay = this.handlePreviousDay.bind(this);
    this.handleNextDay = this.handleNextDay.bind(this);
    this.handleToday = this.handleToday.bind(this);
    this.handleSyncAll = this.handleSyncAll.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this.fetchJobs = this.fetchJobs.bind(this);
    this.calculateStatistics = this.calculateStatistics.bind(this);
    this.updateQueryString = this.updateQueryString.bind(this);
    this.fetchJobsDebounced = debounce(250, false, this.fetchJobs);

    const urlParams = new URLSearchParams(props.location.search);
    const dateParam = urlParams.get('date');

    this.state = {
      jobs: [],
      date: dateParam ? DateTime.fromISO(dateParam) : DateTime.local().startOf("day"),
      totalJobs: 0,
      totalPallets: 0,
      totalWeight: 0,
      totalFreightOnly: 0,
      syncConfirmDialogActive: false
    };
  }

  async componentDidMount() {
    await this.fetchJobs();
  }

  updateQueryString() {
    const params = new URLSearchParams();
    params.set('date', this.state.date.toISODate());

    this.props.history.replace({
      search: params.toString()
    });
  }

  async fetchJobs() {
    try {
      const params = new URLSearchParams();
      params.append('date', this.state.date.toISODate());

      const fetchPromise = (async () => {
        const response = await apiFetch(`/api/radaro/jobs?${params.toString()}`);
        const data = await response?.json();
        if (data && Array.isArray(data)) {
          this.setState({jobs: data}, () => {
            this.calculateStatistics();
          });
        }
      })();

      await tracker.track(fetchPromise);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }

  calculateStatistics() {
    const totalJobs = this.state.jobs.length;
    const totalPallets = this.state.jobs.reduce((sum, job) => sum + job.pallets, 0);
    const totalWeight = this.state.jobs.reduce((sum, job) => sum + job.jobWeight, 0);
    const totalFreightOnly = this.state.jobs.filter(job => job.freightOnly).length;

    this.setState({
      totalJobs,
      totalPallets: Math.round(totalPallets * 10000) / 10000,
      totalWeight: Math.round(totalWeight * 10000) / 10000,
      totalFreightOnly
    });
  }

  handlePreviousDay() {
    this.setState(prevState => ({
      date: prevState.date.minus({days: 1})
    }), () => {
      this.updateQueryString();
      this.fetchJobsDebounced();
    });
  }

  handleNextDay() {
    this.setState(prevState => ({
      date: prevState.date.plus({days: 1})
    }), () => {
      this.updateQueryString();
      this.fetchJobsDebounced();
    });
  }

  handleToday() {
    this.setState({
      date: DateTime.local().startOf("day")
    }, () => {
      this.updateQueryString();
      this.fetchJobsDebounced();
    });
  }

  async handleSyncAll() {
    this.setState({ syncConfirmDialogActive: false });

    try {
      const syncPromise = (async () => {
        const response = await apiFetch('/api/radaro/sync-jobs', {
          method: 'post',
          body: JSON.stringify({ date: this.state.date.toISODate() })
        });

        if (!response?.ok) {
          throw new Error('Sync failed');
        }

        const result = await response.json();
        return result;
      })();

      const result = await tracker.track(syncPromise, "sync-radaro-jobs");

      this.props.uiStore!.notificationUiState.showInfo(
        `Synced ${result.totalOrdersProcessed} order(s) to Radaro`
      );

    } catch (error) {
      this.props.uiStore!.notificationUiState.showError(
        'Error syncing jobs to Radaro. Please try again.'
      );
    }
  }

  async handleRefresh() {
    await this.fetchJobs();
  }

  public render() {
    const columns: MUIDataTableColumnDef[] = [
      {
        name: "orderNumber",
        label: "Order Number",
        options: {
          filter: true,
          sort: true,
          customBodyRender: (value: string) => {
            return <Link to={`/view-order/${value}`} target='_blank'>{value}</Link>;
          }
        }
      },
      {
        name: "radaroJobStatus",
        label: "Radaro Status",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "customerName",
        label: "Customer Name",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "pickupAddress",
        label: "Pickup Address",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "deliveryAddress",
        label: "Delivery Address",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "jobDetail",
        label: "Delivery Details",
        options: {
          filter: false,
          sort: false,
        }
      },
      {
        name: "deliveryTimeSlot",
        label: "Time Slot",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "pallets",
        label: "Pallets",
        options: {
          filter: false,
          sort: true,
          customBodyRender: (value: number) => {
            return value ? value.toFixed(2) : '';
          }
        }
      },
      {
        name: "jobWeight",
        label: "Weight",
        options: {
          filter: false,
          sort: true,
        }
      },
      {
        name: "deliveryTruckType",
        label: "Truck Type",
        options: {
          filter: true,
          sort: true,
        }
      },
      {
        name: "purchaseOrderNumber",
        label: "Purchase Order Number",
        options: {
          filter: false,
          sort: true,
        }
      },
    ];

    return (
      <React.Fragment>
        <Header title="Radaro Jobs"/>
        <section>
          <Grid container className="content radaroJobs">
            <Grid item xs={1}/>
            <Grid item xs={10}>
              <div className='overview'>
                <div className='overview-item'>
                  <Typography variant="h4">{this.state.totalJobs}</Typography>
                  <Typography variant="body1" style={{marginTop: '8px'}}>Total Jobs</Typography>
                </div>
                <div className='overview-item'>
                  <Typography variant="h4">{this.state.totalPallets}</Typography>
                  <Typography variant="body1" style={{marginTop: '8px'}}>Total Pallets</Typography>
                </div>
                <div className='overview-item'>
                  <Typography variant="h4">{this.state.totalWeight}</Typography>
                  <Typography variant="body1" style={{marginTop: '8px'}}>Total Weight</Typography>
                </div>
                <div className='overview-item'>
                  <Typography variant="h4">{this.state.totalFreightOnly}</Typography>
                  <Typography variant="body1" style={{marginTop: '8px'}}>Freight Only Jobs</Typography>
                </div>
              </div>
              <div className='date'>
                <Typography variant="h6">
                  {this.state.date.toFormat('cccc dd/MM/yyyy')}
                </Typography>
                <div className='date-navigator'>
                  <IconButton onClick={this.handlePreviousDay}>
                    <KeyboardArrowLeft/>
                  </IconButton>
                  <Button onClick={this.handleToday}>
                    Today
                  </Button>
                  <IconButton onClick={this.handleNextDay}>
                    <KeyboardArrowRight/>
                  </IconButton>
                </div>
              </div>
              <MUIDataTable
                title={"Radaro Jobs"}
                data={this.state.jobs}
                columns={columns}
                options={{
                  elevation: 0,
                  rowsPerPage: 25,
                  rowsPerPageOptions: [10, 25, 50, 100],
                  selectableRows: false,
                  print: false,
                  download: true,
                  downloadOptions: {
                    filename: `radaro-jobs-${DateTime.local().toFormat('yyyy-MM-dd-HHmm')}.csv`,
                    separator: ','
                  },
                  viewColumns: false,
                }}
              />
            </Grid>
            <Grid item xs={1}/>
          </Grid>
        </section>
        <ActionsMenu>
          <ActionItem label="Refresh Jobs" onClick={this.handleRefresh}>
            <Refresh />
          </ActionItem>
          <ActionItem label="Sync All to Radaro" onClick={() => this.setState({ syncConfirmDialogActive: true })}>
            <Sync />
          </ActionItem>
        </ActionsMenu>
        {this.state.syncConfirmDialogActive && (
          <SyncRadaroConfirmDialog
            onOk={() => this.handleSyncAll()}
            onCancel={() => this.setState({ syncConfirmDialogActive: false })}
          />
        )}
      </React.Fragment>
    );
  }
}

export default withRouter(withAITracking(reactPlugin, RadaroJobs, 'RadaroJobs'));
